import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchPanel", "searchInput", "searchResults", "quickLinks", "mePanel", "meButton", "axlePanel", "axleMessages", "axleInput", "axleSend"]

  connect() {
    this.element.classList.remove("header-collapsed")
    this.searchTimeout = null
    this.searchOpen = false
    this.meOpen = false
    this.axleOpen = false
    this.axleSending = false
    this.boundOnKeydown = this.onDocumentKeydown.bind(this)
    this.boundOnClick = this.onDocumentClick.bind(this)
    document.addEventListener("keydown", this.boundOnKeydown)
    document.addEventListener("click", this.boundOnClick)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundOnKeydown)
    document.removeEventListener("click", this.boundOnClick)
  }

  onDocumentKeydown(event) {
    if (event.key === "Escape" && this.meOpen) this.closeMe()
  }

  onDocumentClick(event) {
    if (!this.meOpen) return
    if (this.hasMeButtonTarget && this.meButtonTarget.contains(event.target)) return
    if (this.hasMePanelTarget && this.mePanelTarget.contains(event.target)) return
    this.closeMe()
  }

  // === Close all other panels ===
  closeOthers(except) {
    if (except !== 'hamburger') {
      const menuEl = document.querySelector("[data-controller~='hamburger']")
      if (menuEl) {
        const hCtrl = this.application.getControllerForElementAndIdentifier(menuEl, "hamburger")
        if (hCtrl && hCtrl.isOpen) hCtrl.close()
      }
    }
    if (except !== 'search' && this.searchOpen) this.closeSearch()
    if (except !== 'me' && this.meOpen) this.closeMe()
    if (except !== 'axle' && this.axleOpen) this.closeAxle()
  }

  // === HAMBURGER ===
  toggleHamburger(event) {
    event?.preventDefault()
    this.closeOthers('hamburger')

    const menuEl = document.querySelector("[data-controller~='hamburger']")
    if (!menuEl) return
    const controller = this.application.getControllerForElementAndIdentifier(menuEl, "hamburger")
    if (!controller) return

    if (controller.isOpen) {
      controller.close()
    } else {
      controller.open()
    }
  }

  // === SEARCH ===
  toggleSearch(event) {
    event?.preventDefault()
    this.closeOthers('search')

    if (this.searchOpen) {
      this.closeSearch()
    } else {
      this.openSearch()
    }
  }

  openSearch() {
    if (!this.hasSearchPanelTarget) return
    this.searchPanelTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.searchPanelTarget.classList.add("search-panel-open")
    })
    this.searchOpen = true
    document.body.style.overflow = "hidden"

    setTimeout(() => {
      if (this.hasSearchInputTarget) this.searchInputTarget.focus()
    }, 100)
  }

  closeSearch() {
    if (!this.hasSearchPanelTarget) return
    this.searchPanelTarget.classList.remove("search-panel-open")
    this.searchOpen = false
    document.body.style.overflow = ""

    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add("hidden")
      this.searchResultsTarget.innerHTML = ""
    }
    if (this.hasQuickLinksTarget) this.quickLinksTarget.classList.remove("hidden")

    setTimeout(() => {
      if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.add("hidden")
    }, 260)
  }

  onSearchInput(event) {
    const query = event.target.value.trim()
    clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      if (this.hasSearchResultsTarget) {
        this.searchResultsTarget.classList.add("hidden")
        this.searchResultsTarget.innerHTML = ""
      }
      if (this.hasQuickLinksTarget) this.quickLinksTarget.classList.remove("hidden")
      return
    }

    if (this.hasQuickLinksTarget) this.quickLinksTarget.classList.add("hidden")
    this.searchTimeout = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    try {
      const res = await fetch(`/search/suggestions?q=${encodeURIComponent(query)}`)
      const data = await res.json()
      if (!this.hasSearchResultsTarget) return

      if (data.results && data.results.length > 0) {
        this.searchResultsTarget.innerHTML = data.results.slice(0, 8).map(r => `
          <a href="${r.url}" class="search-panel-link">
            ${r.image
              ? `<img src="${r.image}" class="org-logo org-logo--tiny" style="max-height:1.5rem;max-width:5rem;width:auto;height:auto;object-fit:contain;flex-shrink:0;">`
              : `<i class="${r.icon}" style="color: rgba(255,255,255,0.5);width:32px;text-align:center;flex-shrink:0;"></i>`
            }
            <div style="flex:1;min-width:0;">
              <div class="spl-title">${this.esc(r.title)}</div>
              <div class="spl-sub">${this.esc(r.subtitle || '')}</div>
            </div>
          </a>
        `).join("") + `
          <a href="/search?q=${encodeURIComponent(query)}" style="display:block;text-align:center;padding:12px;margin:4px 12px 8px;border-radius:12px;background:rgba(59,130,246,0.08);color:#60a5fa;font-size:13px;font-weight:600;text-decoration:none;">
            View all results →
          </a>`
      } else {
        this.searchResultsTarget.innerHTML = `
          <div style="text-align:center;padding:24px 16px;color:rgba(255,255,255,0.3);font-size:13px;">
            No results for "${this.esc(query)}"
          </div>`
      }
      this.searchResultsTarget.classList.remove("hidden")
    } catch(e) { console.error("Search:", e) }
  }

  // === ME ===
  toggleMe(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.closeOthers('me')

    if (this.meOpen) {
      this.closeMe()
    } else {
      this.openMe()
    }
  }

  openMe() {
    if (!this.hasMePanelTarget) return
    this.meHideGeneration = (this.meHideGeneration || 0) + 1
    this.mePanelTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.mePanelTarget.classList.add("me-panel-open")
    })
    this.meOpen = true
    this.setMeExpanded(true)
    document.body.style.overflow = "hidden"
  }

  closeMe() {
    if (!this.hasMePanelTarget) return
    this.mePanelTarget.classList.remove("me-panel-open")
    this.meOpen = false
    this.setMeExpanded(false)
    document.body.style.overflow = ""
    const generation = this.meHideGeneration
    setTimeout(() => {
      if (this.meOpen) return
      if (generation !== this.meHideGeneration) return
      if (this.hasMePanelTarget) this.mePanelTarget.classList.add("hidden")
    }, 260)
  }

  setMeExpanded(open) {
    if (!this.hasMeButtonTarget) return
    this.meButtonTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  // === AXLE MINI CHAT ===
  toggleAxle(event) {
    event?.preventDefault()
    this.closeOthers('axle')
    if (this.axleOpen) { this.closeAxle() } else { this.openAxle() }
  }

  openAxle() {
    if (!this.hasAxlePanelTarget) return
    this.axlePanelTarget.classList.remove("hidden")
    requestAnimationFrame(() => { this.axlePanelTarget.classList.add("axle-panel-open") })
    this.axleOpen = true
    document.body.style.overflow = "hidden"
    setTimeout(() => { if (this.hasAxleInputTarget) this.axleInputTarget.focus() }, 300)
  }

  closeAxle() {
    if (!this.hasAxlePanelTarget) return
    this.axlePanelTarget.classList.remove("axle-panel-open")
    this.axleOpen = false
    document.body.style.overflow = ""
    setTimeout(() => { if (this.hasAxlePanelTarget) this.axlePanelTarget.classList.add("hidden") }, 260)
  }

  onAxleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendAxleMessage()
    }
  }

  async sendAxleMessage() {
    if (!this.hasAxleInputTarget || this.axleSending) return
    const msg = this.axleInputTarget.value.trim()
    if (!msg) return

    this.axleSending = true
    this.axleInputTarget.value = ""
    if (this.hasAxleSendTarget) this.axleSendTarget.disabled = true

    const messagesEl = this.axleMessagesTarget
    // Clear welcome if present
    const welcome = messagesEl.querySelector(".axle-mini-welcome")
    if (welcome) welcome.remove()

    // Add user bubble
    messagesEl.innerHTML += `<div class="axle-mini-bubble user"><div class="axle-mini-bubble-inner">${this.esc(msg)}</div></div>`

    // Add typing indicator
    const typingId = `typing-${Date.now()}`
    messagesEl.innerHTML += `<div class="axle-mini-bubble axle" id="${typingId}"><div class="axle-mini-bubble-inner"><div class="axle-mini-typing"><span></span><span></span><span></span></div></div></div>`
    messagesEl.scrollTop = messagesEl.scrollHeight

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch("/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ message: msg })
      })
      const data = await resp.json()
      const typing = document.getElementById(typingId)
      if (typing) typing.remove()

      if (data.success && data.messages) {
        const axleMsg = data.messages.find(m => m.role === "assistant") || data.messages[data.messages.length - 1]
        if (axleMsg) {
          messagesEl.innerHTML += `<div class="axle-mini-bubble axle"><div class="axle-mini-bubble-inner">${axleMsg.formatted_content || this.esc(axleMsg.content)}</div></div>`
        }
      } else {
        messagesEl.innerHTML += `<div class="axle-mini-bubble axle"><div class="axle-mini-bubble-inner" style="color: rgba(239,68,68,0.7);">Something went wrong. Try the <a href="/chat" style="color: #34d399;">full chat</a>.</div></div>`
      }
    } catch (e) {
      const typing = document.getElementById(typingId)
      if (typing) typing.remove()
      messagesEl.innerHTML += `<div class="axle-mini-bubble axle"><div class="axle-mini-bubble-inner" style="color: rgba(239,68,68,0.7);">Connection error. Try the <a href="/chat" style="color: #34d399;">full chat</a>.</div></div>`
    }

    messagesEl.scrollTop = messagesEl.scrollHeight
    this.axleSending = false
    if (this.hasAxleSendTarget) this.axleSendTarget.disabled = false
    if (this.hasAxleInputTarget) this.axleInputTarget.focus()
  }

  esc(t) { const d = document.createElement("div"); d.textContent = t; return d.innerHTML }
}
